import { createContext, useContext, useMemo } from "react";
import {
  type AccidentReportViewModel,
  VehicleKey,
} from "#accident-statement/schema";
import type { FieldSave } from "./FieldSave";
import { capitalize } from "../shared/utils/stringMutation";

export interface IReportContext {
  report: AccidentReportViewModel;
  party: VehicleKey;
  otherParty: VehicleKey;
  applyReportUpdates(
    updates: (prev: AccidentReportViewModel) => AccidentReportViewModel,
  ): void;
  onChange(report: AccidentReportViewModel): void;
  saveField(fields: FieldSave): Promise<void>;
  saveFields(fields: FieldSave[]): Promise<void>;
  enqueueChanges(fields: FieldSave[]): void;
}

export const ReportContext = createContext<IReportContext | null>(null);

export function useReportContext() {
  const reportContext = useContext(ReportContext);

  if (!reportContext) throw new Error("No ReportContext found!");

  return reportContext;
}

export function useReport() {
  const reportContext = useReportContext();

  const { report } = reportContext;

  if (!report) throw new Error("No report found!");

  return report;
}

export function useVehicleReport() {
  const report = useReport();
  const party = useParty();
  const vehicleReport =
    party === VehicleKey.A ? report.vehicleA : report.vehicleB;

  return vehicleReport;
}

export function usePageTwo() {
  const report = useReport();
  const vehicleReport =
    "pageTwoA" in report ? report.pageTwoA : report.pageTwoB;

  return vehicleReport;
}

export function useParty() {
  const reportContext = useReportContext();

  const { party } = reportContext;

  if (party === null) throw new Error("No Party loaded!");

  return party;
}

export function useOtherParty() {
  const party = useParty();
  return getOtherParty(party);
}

export function getOtherParty(party: VehicleKey) {
  return party === VehicleKey.A ? VehicleKey.B : VehicleKey.A;
}

export const PARTY_NAMES = {
  [VehicleKey.A]: "part A",
  [VehicleKey.B]: "part B",
};

export function getPartyName(party: VehicleKey) {
  const roleName = PARTY_NAMES[party];
  return {
    titleCase: capitalize(roleName),
    regularCase: roleName,
  };
}

export function usePartyName() {
  const party = useParty();
  const otherParty = useOtherParty();

  return useMemo(() => {
    const self = getPartyName(party);
    const other = getPartyName(otherParty);

    return {
      Other: other.titleCase,
      other: other.regularCase,
      Self: self.titleCase,
      self: self.regularCase,
    };
  }, [party, otherParty]);
}
